import { useNavigate, useParams } from 'react-router-dom';
import { LoadingState, Space, useLoading } from '@monorepo/react-components';
import { Button, Card, Tooltip, Typography } from 'antd';
import React, { useEffect, useRef } from 'react';
import { BulbOutlined, EditOutlined } from '@ant-design/icons';
import { SeriesSettingsModal, SeriesSettingsRef } from './series-settings';
import { useStore } from '../../../helpers/use-store';
import { SeriesInformation } from '../series-information';
import { observer } from 'mobx-react-lite';
import SeriesVideos, { SeriesVideosRef } from '../series-videos/series-videos';
import { useNotification } from '../../../hooks/use-notification';
import { useResponsiveReady } from '../../../hooks/use-responive-ready';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const SeriesPage = () => {
  const { t } = useTranslation();
  const modalRef = useRef<SeriesSettingsRef>(null);
  const seriesVideosRef = useRef<SeriesVideosRef>(null);
  const {
    loadingState: generateTitlesLoadingState,
    updateLoadingState: updateGenerateTitlesLoadingState,
  } = useLoading();
  const {
    dataStore: { seriesStore },
  } = useStore();
  const navigate = useNavigate();
  const notificationApi = useNotification();
  const { seriesId } = useParams();
  const series = seriesStore.get(seriesId || '') || null;
  const { isMobile } = useResponsiveReady();

  useEffect(() => {
    if (!series) {
      seriesStore.fetchAll();
    }
  }, [seriesId]);

  const openModal = () => {
    modalRef.current?.open();
  };

  if (!series) {
    navigate('/series');
    return null;
  }

  const generateNextTitles = async () => {
    try {
      updateGenerateTitlesLoadingState(LoadingState.Loading);
      await seriesStore.generateNextVideosMetadata(series._id);
      seriesVideosRef.current?.reFetch(1);

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('series.page.generateSuccess'),
      });
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        icon: '',
        message: t('common.error'),
        description: t('series.page.generateError'),
      });

      console.error(`failed generating next videos titles`, e);
    } finally {
      updateGenerateTitlesLoadingState(LoadingState.Loaded);
    }
  };

  return (
    <Space direction="vertical">
      <Helmet>
        <title>{series.name} Series | ShortsNinja</title>
        <meta
          name="description"
          content="Manage your video series. Control multiple faceless videos and AI voiceover settings in one place."
        />
        <meta
          name="keywords"
          content="series details, ShortsNinja, faceless videos, ai video automation, ai voiceover"
        />
      </Helmet>
      <Typography.Title level={3}>{t('series.page.details')}</Typography.Title>
      <SeriesInformation
        columns={isMobile ? 1 : 2}
        actions={
          <div style={{ marginTop: isMobile ? '15px' : 0 }}>
            <Tooltip
              placement={'bottom'}
              title={t('series.page.generateTooltip')}
            >
              <Button
                loading={generateTitlesLoadingState === LoadingState.Loading}
                icon={<BulbOutlined />}
                onClick={generateNextTitles}
              >
                {t('series.page.generateNextTitles')}
              </Button>
            </Tooltip>
            <Button icon={<EditOutlined />} onClick={openModal}>
              {t('series.page.edit')}
            </Button>
          </div>
        }
        series={series}
      />

      <Typography.Title level={3}>{t('series.page.videos')}</Typography.Title>
      <Card>
        <SeriesVideos ref={seriesVideosRef} series={series} />
      </Card>
      <SeriesSettingsModal seriesId={series._id} ref={modalRef} />
    </Space>
  );
};

export default observer(SeriesPage);
